import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cn(
        "h-10 w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink",
        "placeholder:text-ink-subtle focus:border-ink-subtle focus:outline-none focus:ring-3 focus:ring-line",
        "aria-invalid:border-danger aria-invalid:ring-danger-soft",
        className,
      )}
      {...props}
    />
  );
}

/** Native select styled like `Input`. */
export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={cn(
        "h-10 w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink",
        "focus:border-ink-subtle focus:outline-none focus:ring-3 focus:ring-line",
        "aria-invalid:border-danger aria-invalid:ring-danger-soft",
        className,
      )}
      {...props}
    />
  );
}

/** Multi-line text styled like `Input`. */
export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cn(
        "min-h-20 w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm font-normal text-ink",
        "placeholder:text-ink-subtle focus:border-ink-subtle focus:outline-none focus:ring-3 focus:ring-line",
        "aria-invalid:border-danger aria-invalid:ring-danger-soft",
        className,
      )}
      {...props}
    />
  );
}

/** Label + control + optional hint/error. */
export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: ReactNode;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-[13px] font-medium">
      {label}
      {children}
      {error ? (
        <span className="font-normal text-danger-ink">{error}</span>
      ) : (
        hint && <span className="font-normal text-ink-muted">{hint}</span>
      )}
    </label>
  );
}
